class JeProductHero extends HTMLElement {
  connectedCallback() {
    const sectionId = this.dataset.section;
    this.sectionId = sectionId;
    this.variantSelect = document.getElementById(`JeVariantSelect-${sectionId}`);
    this.quantityInput = document.getElementById(`JeQuantityInput-${sectionId}`);
    // Dawn's <quantity-input> stepper buttons stop at min, but typing
    // directly into the field (or clearing it) bypasses that entirely.
    // Clamp on blur, not on every keystroke — clamping live would reset
    // the field to 1 mid-typing (e.g. clearing "3" to type "5" passes
    // through an empty value first) and the next digit would land after
    // the reset "1" instead of replacing it.
    if (this.quantityInput) {
      this.quantityInput.addEventListener('blur', () => {
        const val = parseInt(this.quantityInput.value, 10);
        if (isNaN(val) || val < 1) {
          this.quantityInput.value = 1;
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
    this.form = document.getElementById(`JeProductForm-${sectionId}`);
    this.cardContainer = document.getElementById(`JePlanCards-${sectionId}`);
    this.toggle = document.getElementById(`JePlanToggle-${sectionId}`);
    this.priceEl = this.querySelector('.je-product-hero__price-value');
    this.planNameEl = this.querySelector('.je-product-hero__plan-name');
    this.unlimitedNote = document.getElementById(`JeUnlimitedNote-${sectionId}`);

    this.stickyBar = document.getElementById(`JeStickyBar-${sectionId}`);
    this.stickyPriceEl = this.stickyBar ? this.stickyBar.querySelector('.je-sticky-bar__price') : null;
    this.stickyPlanNameEl = this.stickyBar ? this.stickyBar.querySelector('.je-sticky-bar__plan-name') : null;
    this.stickyAddToCartBtn = document.getElementById(`JeStickyAddToCart-${sectionId}`);

    const raw = document.getElementById(`JeProductVariantData-${sectionId}`);
    const priceRaw = document.getElementById(`JeVariantPrices-${sectionId}`);
    if (!raw || !this.variantSelect) return;

    const stampEl = document.getElementById(`je-stamp-path-${sectionId}`);
    this.stampPath = stampEl ? stampEl.getAttribute('d') : '';

    const moneyFormatEl = document.getElementById(`JeMoneyFormat-${sectionId}`);
    this.moneyFormat = moneyFormatEl ? JSON.parse(moneyFormatEl.textContent) : '${{amount}}';

    const unlimitedRaw = document.getElementById(`JeUnlimitedData-${sectionId}`);
    this.unlimited = unlimitedRaw ? JSON.parse(unlimitedRaw.textContent) : null;
    // 365 matches eSIM Access's own top discount tier ceiling ("30-365 days").
    this.unlimitedMinDays = 1;
    this.unlimitedMaxDays = 365;
    this.unlimitedDays = 7;

    this.allVariants = JSON.parse(raw.textContent);
    // Price map: variantId → Liquid-formatted money string (respects store currency)
    this.priceMap = priceRaw ? JSON.parse(priceRaw.textContent) : {};
    this.parsed = this.parseVariants(this.allVariants);
    this.currentMode = 'standard';

    if (this.parsed.hasNonhkip) this.toggle.removeAttribute('hidden');
    this.setupToggle();
    this.renderCards('standard');
    this.initRecommender();

    if (this.form) this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    this.setupStickyBar();
  }

  // The sticky bar never owns its own quantity or variant state — it just
  // mirrors the buy box's price/plan text (set alongside them in
  // applyVariant) and submits the same form, so there's one source of
  // truth. It shows once the real "Add to cart" button scrolls out of
  // view and hides again when it's back on screen.
  setupStickyBar() {
    if (!this.stickyBar) return;

    // Both the sticky bar's button and the main buy box button add to cart
    // and open the cart drawer (Holafly-style — see Knowledge for the
    // order-history check that backed this: 97% of orders are a single
    // destination, so a fast add > drawer-with-checkout-button flow beats
    // both a bare "Add to cart" with no next step and a hard redirect
    // straight to checkout with no way to add a second destination).
    // handleSubmit() below is the single source of truth; the sticky
    // button just triggers the same logic as a real form submit.
    if (this.stickyAddToCartBtn) {
      this.stickyAddToCartBtn.addEventListener('click', () => {
        if (!this.form) return;
        this.handleSubmit({ preventDefault: () => {} });
      });
    }

    const atcRow = this.querySelector('.je-product-hero__atc-row');
    if (!atcRow || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.stickyBar.hidden = true;
          return;
        }
        // Only reveal once the real button has been scrolled past (its
        // bottom edge is above the viewport) — not simply because it
        // hasn't been scrolled to yet (its top edge is still below the
        // viewport, which is also "not intersecting" on initial load).
        if (entry.boundingClientRect.bottom < 0) {
          this.stickyBar.hidden = false;
        }
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(atcRow);
  }

  // Both the main "Add to cart" button and the sticky bar's button call
  // this. Quantity always means "number of eSIMs" (matching every other
  // product on the site). Fixed-GB plans add to cart the same way Dawn's
  // own product-form.js does — a FormData POST to routes.cart_add_url,
  // requesting re-rendered `sections` alongside the add so the cart
  // drawer's contents update via AJAX with no page reload. The Unlimited
  // plan is the exception: quantity (eSIMs) and days are two independent
  // numbers, and Shopify's line-item quantity can only carry one meaning
  // per line, so it submits one line item per eSIM, each with quantity set
  // to the day count, via a JSON POST to the same endpoint (still
  // requesting `sections`). Either way, a successful add opens the cart
  // drawer instead of navigating anywhere — see je-cart-drawer.css for the
  // mobile-only "drops from the top, doesn't cover the screen" styling
  // layered on top of Dawn's existing drawer open/close/render logic.
  handleSubmit(e) {
    e.preventDefault();

    const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const submitButton = document.getElementById(`JeAddToCart-${this.sectionId}`);
    if (submitButton) submitButton.setAttribute('aria-disabled', 'true');

    const openCart = (response) => {
      if (submitButton) submitButton.removeAttribute('aria-disabled');
      if (cartEl && typeof cartEl.renderContents === 'function') {
        // Dawn's own product-form.js strips this class after a successful
        // add (assets/product-form.js:104) — cart.js's updateQuantity()
        // adds it back when the cart empties via the drawer's own
        // remove/quantity controls, but nothing removes it again on this
        // custom add-to-cart path, since it never runs product-form.js at
        // all. Left stale, Dawn's CSS
        // (component-cart-drawer.css:47-56) keeps treating the drawer as
        // empty — hidden header, centered/collapsed layout — even once it
        // has items again, which is exactly the "not fully visible after
        // emptying and re-adding" bug.
        cartEl.classList.remove('is-empty');
        cartEl.renderContents(response);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    };

    const onError = (err) => {
      console.error('Add to cart failed', err);
      if (submitButton) submitButton.removeAttribute('aria-disabled');
    };

    if (!this.currentIsUnlimited) {
      if (!this.form) return;
      const formData = new FormData(this.form);
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        formData.append('sections', cartEl.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
      }

      fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) return Promise.reject(response);
          openCart(response);
        })
        .catch(onError);
      return;
    }

    const esimCount = parseInt(this.quantityInput.value, 10) || 1;
    const items = [];
    for (let i = 1; i <= esimCount; i++) {
      const item = { id: this.unlimited.variantId, quantity: this.unlimitedDays };
      // Shopify merges identical variant + quantity line items into one,
      // summing their quantities — which would turn "2 eSIMs of 7 days"
      // into "1 eSIM of 14 days". A distinct property per slot stops that
      // merge; it has no effect on price.
      if (esimCount > 1) item.properties = { _esim_slot: String(i) };
      items.push(item);
    }

    const body = { items };
    if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
      body.sections = cartEl.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then((response) => {
        if (!response.ok) return response.json().then((data) => Promise.reject(data));
        return response.json();
      })
      .then(openCart)
      .catch(onError);
  }

  // eSIM Access's own tiered day-count discount on daily-cap packages.
  discountForDays(days) {
    if (days >= 30) return 0.18;
    if (days >= 20) return 0.15;
    if (days >= 10) return 0.11;
    if (days >= 5) return 0.08;
    return 0.04;
  }

  // Mirrors Shopify's own money_format placeholder syntax so client-computed
  // prices (like the Unlimited plan's day-count total) match the store's
  // configured currency display exactly. Do not use Intl.NumberFormat's
  // currency style here — it inserts disambiguating prefixes like "A$"/"C$"
  // that Shopify's own formatting never shows.
  formatWithDelimiters(cents, precision) {
    const rate = (window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;
    const value = (cents / 100) * rate;
    const parts = value.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return precision > 0 ? parts.join('.') : parts[0];
  }

  formatCents(cents) {
    const format = this.moneyFormat || '${{amount}}';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = this.formatWithDelimiters(cents, 0);
        break;
      default:
        formatted = this.formatWithDelimiters(cents, 2);
    }
    return format.replace(match[0], formatted);
  }

  unlimitedTotalCents(days) {
    const discount = this.discountForDays(days);
    const effectiveUnitCents = Math.round(this.unlimited.unitPriceCents * (1 - discount));
    return { totalCents: effectiveUnitCents * days, effectiveUnitCents, discount };
  }

  formatPrice(variantId) {
    return this.priceMap[variantId] || '';
  }

  parseVariants(variants) {
    const standard = [];
    const nonhkip = [];

    variants.forEach(v => {
      const match = v.title.match(/(\d+(?:\.\d+)?)\s*(MB|GB)\s+(\d+)\s*Days/i);
      if (!match) return;
      const entry = {
        id: String(v.id),
        data: parseFloat(match[1]),
        unit: match[2].toUpperCase(),
        days: parseInt(match[3], 10),
        available: v.available !== false,
        isNonhkip: v.title.includes('(nonhkip)')
      };
      if (entry.isNonhkip) nonhkip.push(entry);
      else standard.push(entry);
    });

    return { standard, nonhkip, hasNonhkip: nonhkip.length > 0 };
  }

  groupByDays(variants) {
    const groups = {};
    variants.forEach(v => {
      if (!groups[v.days]) groups[v.days] = [];
      groups[v.days].push(v);
    });
    return groups;
  }

  renderCards(mode) {
    const variants = this.parsed[mode] || this.parsed.standard;
    const groups = this.groupByDays(variants);
    const sortedDays = Object.keys(groups).map(Number).sort((a, b) => a - b);

    if (sortedDays.length === 0 && !this.unlimited) {
      this.cardContainer.innerHTML = '<p>No plans available.</p>';
      return;
    }

    const count = sortedDays.length + (this.unlimited ? 1 : 0);
    this.cardContainer.className = 'je-plan-cards';
    // Column rules: no orphaned single cards on a row
    // 1→1, 2→2, 3→3, 4→4, 5→3(3+2), 6→3(3+3), 7→4(4+3), 8→4(4+4)
    const cols = count <= 4 ? count : count <= 6 ? 3 : 4;
    this.cardContainer.classList.add(`je-plan-cards--cols-${cols}`);

    const fixedCardsHtml = sortedDays.map((days, idx) => {
      const group = groups[days];
      group.sort((a, b) => a.data - b.data);
      const first = group[0];
      const hasOptions = group.length > 1;
      const isSelected = idx === 0;
      const displayPrice = this.formatPrice(first.id);

      return `
        <div class="je-plan-card-slot">
          <div class="je-card-eyebrow">&nbsp;</div>
          <div class="je-plan-card${isSelected ? ' je-plan-card--selected' : ''}"
               data-days="${days}"
               data-variant-id="${first.id}"
               role="radio"
               aria-checked="${isSelected}"
               tabindex="0">
            <svg class="je-plan-card__stamp" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
              <path class="je-plan-card__outline" d="${this.stampPath}"/>
            </svg>
            <div class="je-plan-card__face">
              <div class="je-plan-card__header">
                <div class="je-plan-card__price">${displayPrice}</div>
                <div class="je-plan-card__radio"></div>
              </div>
              <div class="je-plan-card__duration">${days} days</div>
              ${hasOptions
                ? `<select class="je-plan-card__select" aria-label="Data amount">
                    ${group.map(v =>
                      `<option value="${v.id}" data-variant-id="${v.id}">${v.data} ${v.unit}</option>`
                    ).join('')}
                  </select>`
                : `<div class="je-plan-card__data">${first.data} ${first.unit}</div>`
              }
            </div>
          </div>
        </div>`;
    }).join('');

    this.cardContainer.innerHTML = fixedCardsHtml + (this.unlimited ? this.renderUnlimitedCard() : '');

    if (this.unlimited) {
      this.updateUnlimitedCardDisplay(this.cardContainer.querySelector('[data-unlimited="true"]'));
    }

    this.bindCardEvents();

    if (sortedDays.length > 0) {
      const firstGroup = groups[sortedDays[0]];
      this.applyVariant(firstGroup[0].id);
    } else {
      this.applyVariant(this.unlimited.variantId);
    }
  }

  renderUnlimitedCard() {
    return `
      <div class="je-plan-card-slot">
        <div class="je-card-eyebrow je-card-eyebrow--unlimited">Unlimited</div>
        <div class="je-plan-card je-plan-card--unlimited"
             data-variant-id="${this.unlimited.variantId}"
             data-unlimited="true"
             role="radio"
             aria-checked="false"
             tabindex="0">
          <svg class="je-plan-card__stamp" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
            <path class="je-plan-card__outline" d="${this.stampPath}"/>
          </svg>
          <div class="je-plan-card__face">
            <div class="je-plan-card__header">
              <div class="je-plan-card__price" data-el="unlimited-total"></div>
              <div class="je-plan-card__radio"></div>
            </div>
            <div class="je-unlimited-rate" data-el="unlimited-rate"></div>
            <div class="je-unlimited-days-label">Select days</div>
            <div class="je-unlimited-days-row">
              <button type="button" class="je-unlimited-step" data-step="-1" aria-label="Fewer days">&minus;</button>
              <input type="number" class="je-plan-card__select je-unlimited-days-input" min="${this.unlimitedMinDays}" max="${this.unlimitedMaxDays}" value="${this.unlimitedDays}" aria-label="Select number of days">
              <button type="button" class="je-unlimited-step" data-step="1" aria-label="More days">&plus;</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  updateUnlimitedCardDisplay(card) {
    const { totalCents, effectiveUnitCents } = this.unlimitedTotalCents(this.unlimitedDays);
    card.querySelector('[data-el="unlimited-total"]').textContent = this.formatCents(totalCents);
    // At 1 day the per-day rate is identical to the total above it — showing
    // both is redundant, so hide the rate line in that case only.
    const rateEl = card.querySelector('[data-el="unlimited-rate"]');
    if (this.unlimitedDays === 1) {
      rateEl.hidden = true;
    } else {
      rateEl.textContent = `${this.formatCents(effectiveUnitCents)}/day`;
      rateEl.hidden = false;
    }
  }

  bindCardEvents() {
    this.cardContainer.querySelectorAll('.je-plan-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION' || e.target.tagName === 'INPUT') return;
        this.selectCard(card);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.selectCard(card);
        }
      });
    });

    this.cardContainer.querySelectorAll('.je-plan-card__select').forEach(select => {
      if (select.classList.contains('je-unlimited-days-input')) return;
      select.addEventListener('change', (e) => {
        e.stopPropagation();
        const card = select.closest('.je-plan-card');
        const variantId = select.selectedOptions[0].dataset.variantId;
        const priceEl = card.querySelector('.je-plan-card__price');
        card.dataset.variantId = variantId;
        if (priceEl) priceEl.textContent = this.formatPrice(variantId);
        this.selectCard(card);
      });
    });

    const daysInput = this.cardContainer.querySelector('.je-unlimited-days-input');
    if (daysInput) {
      daysInput.addEventListener('click', (e) => e.stopPropagation());

      // Focusing the field selects the Unlimited card, same as interacting
      // with the GB dropdown selects its fixed-GB card above.
      daysInput.addEventListener('focus', () => {
        const card = daysInput.closest('.je-plan-card');
        if (!card.classList.contains('je-plan-card--selected')) this.selectCard(card);
      });

      // While the field holds a valid in-range number, update live. While it's
      // empty or out of range (mid-edit), leave the last valid price showing
      // rather than snapping to a fallback — a price changing under an empty
      // box reads as broken. Clamping to [min, max] only happens on blur.
      daysInput.addEventListener('input', () => {
        const val = parseInt(daysInput.value, 10);
        if (!Number.isFinite(val) || val < this.unlimitedMinDays || val > this.unlimitedMaxDays) return;
        this.unlimitedDays = val;
        const card = daysInput.closest('.je-plan-card');
        this.updateUnlimitedCardDisplay(card);
        if (card.classList.contains('je-plan-card--selected')) this.applyVariant(this.unlimited.variantId);
      });

      daysInput.addEventListener('blur', () => {
        const val = parseInt(daysInput.value, 10);
        const clamped = !Number.isFinite(val) ? this.unlimitedMinDays
          : Math.min(this.unlimitedMaxDays, Math.max(this.unlimitedMinDays, val));
        daysInput.value = clamped;
        this.unlimitedDays = clamped;
        const card = daysInput.closest('.je-plan-card');
        this.updateUnlimitedCardDisplay(card);
        if (card.classList.contains('je-plan-card--selected')) this.applyVariant(this.unlimited.variantId);
      });

      this.cardContainer.querySelectorAll('.je-unlimited-step').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const card = btn.closest('.je-plan-card');
          const delta = parseInt(btn.dataset.step, 10);
          const next = Math.min(this.unlimitedMaxDays, Math.max(this.unlimitedMinDays, this.unlimitedDays + delta));
          this.unlimitedDays = next;
          daysInput.value = next;
          this.updateUnlimitedCardDisplay(card);
          if (card.classList.contains('je-plan-card--selected')) {
            this.applyVariant(this.unlimited.variantId);
          } else {
            this.selectCard(card);
          }
        });
      });
    }
  }

  selectCard(card) {
    this.cardContainer.querySelectorAll('.je-plan-card').forEach(c => {
      c.classList.remove('je-plan-card--selected');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('je-plan-card--selected');
    card.setAttribute('aria-checked', 'true');

    if (card.dataset.unlimited === 'true') {
      this.applyVariant(this.unlimited.variantId);
      return;
    }

    const select = card.querySelector('.je-plan-card__select');
    const variantId = select ? select.selectedOptions[0].dataset.variantId : card.dataset.variantId;
    this.applyVariant(variantId);
  }

  applyVariant(variantId) {
    const isUnlimited = this.unlimited && String(variantId) === String(this.unlimited.variantId);
    this.currentIsUnlimited = isUnlimited;

    if (this.variantSelect) {
      this.variantSelect.value = variantId;
      this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.priceEl) {
      this.priceEl.textContent = isUnlimited
        ? this.formatCents(this.unlimitedTotalCents(this.unlimitedDays).totalCents)
        : this.formatPrice(variantId);
    }

    if (this.stickyPriceEl) {
      this.stickyPriceEl.textContent = this.priceEl ? this.priceEl.textContent : '';
    }

    if (this.unlimitedNote) {
      if (isUnlimited) {
        this.unlimitedNote.textContent =
          `${this.unlimited.dailyCapGb}GB high-speed data every day, then ${this.unlimited.throttleSpeed} until it resets the next day.`;
        this.unlimitedNote.hidden = false;
      } else {
        this.unlimitedNote.hidden = true;
      }
    }

    if (isUnlimited) {
      if (this.planNameEl) {
        const dayWord = this.unlimitedDays === 1 ? 'day' : 'days';
        const { effectiveUnitCents, discount } = this.unlimitedTotalCents(this.unlimitedDays);
        // No save framing at the 1-4 day tier — eSIM Access's 4% there is
        // just their base rate, not a real "buy more, save more" discount.
        const parens = this.unlimitedDays >= 5
          ? `(${this.formatCents(effectiveUnitCents)}/day - Save ${Math.round(discount * 100)}%)`
          : `(${this.formatCents(effectiveUnitCents)}/day)`;
        this.planNameEl.textContent = `Unlimited ${this.unlimitedDays} ${dayWord} ${parens}`;
      }
      if (this.stickyPlanNameEl) this.stickyPlanNameEl.textContent = this.planNameEl ? this.planNameEl.textContent : '';
      return;
    }

    if (this.planNameEl) {
      const variant = this.allVariants.find(v => String(v.id) === String(variantId));
      if (variant) this.planNameEl.textContent = variant.title;
    }
    if (this.stickyPlanNameEl) this.stickyPlanNameEl.textContent = this.planNameEl ? this.planNameEl.textContent : '';
  }

  initRecommender() {
    const el = document.getElementById(`JeRecommender-${this.sectionId}`);
    if (!el) return;
    this.recommender = el;

    const toggle = el.querySelector('.je-recommender__toggle');
    const panel = el.querySelector('.je-recommender__panel');
    const submit = el.querySelector('.je-recommender__submit');
    const daysInput = el.querySelector('.je-recommender__days-field');

    toggle.addEventListener('click', () => {
      const isOpen = !panel.hidden;
      panel.hidden = isOpen;
      toggle.setAttribute('aria-expanded', String(!isOpen));
    });

    const checkReady = () => {
      const daysOk = daysInput && parseInt(daysInput.value, 10) >= 1;
      const pillOk = !!el.querySelector('.je-recommender__pill--active');
      submit.disabled = !(daysOk && pillOk);
    };

    if (daysInput) daysInput.addEventListener('input', checkReady);

    el.querySelectorAll('.je-recommender__pills').forEach(group => {
      group.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => {
          group.querySelectorAll('button').forEach(b => b.classList.remove('je-recommender__pill--active'));
          btn.classList.add('je-recommender__pill--active');
          checkReady();
        });
      });
    });

    submit.addEventListener('click', () => this.recommend());
  }

  recommend() {
    const el = this.recommender;
    const exactDays = parseInt(el.querySelector('.je-recommender__days-field').value, 10);
    const dailyMb = parseInt(el.querySelector('[data-group="usage"] .je-recommender__pill--active').dataset.value, 10);
    const neededGb = Math.max(0.5, (dailyMb * exactDays) / 1000);

    const variants = this.parsed[this.currentMode] || this.parsed.standard;
    const groups = this.groupByDays(variants);
    const sortedDays = Object.keys(groups).map(Number).sort((a, b) => a - b);
    const toGb = v => v.unit === 'MB' ? v.data / 1000 : v.data;

    const bestInGroup = g => {
      const sorted = g.slice().sort((a, b) => toGb(a) - toGb(b));
      const gbOnly = sorted.filter(v => v.unit === 'GB');
      return sorted.find(v => toGb(v) >= neededGb)
        || (gbOnly.length ? gbOnly[gbOnly.length - 1] : sorted[sorted.length - 1]);
    };

    // Find the shortest plan that covers the trip length
    let matchedDays = sortedDays.find(d => d >= exactDays) || sortedDays[sortedDays.length - 1];
    let primary = bestInGroup(groups[matchedDays]);

    // If data need isn't met, escalate to next tier up
    if (toGb(primary) < neededGb) {
      const nextTier = sortedDays.find(d => d > matchedDays);
      if (nextTier) {
        const nextBest = bestInGroup(groups[nextTier]);
        if (toGb(nextBest) > toGb(primary)) {
          matchedDays = nextTier;
          primary = nextBest;
        }
      }
    }

    // Select the card
    const card = this.cardContainer.querySelector(`[data-days="${matchedDays}"]`);
    if (card) {
      const select = card.querySelector('.je-plan-card__select');
      if (select) {
        select.value = primary.id;
        card.dataset.variantId = primary.id;
        const priceEl = card.querySelector('.je-plan-card__price');
        if (priceEl) priceEl.textContent = this.formatPrice(primary.id);
      }
      this.selectCard(card);
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // Same-tier alternatives: other options in the matched day tier with more data
    const sameTierSorted = groups[matchedDays].slice().sort((a, b) => toGb(a) - toGb(b));
    const sameTierAlts = sameTierSorted.filter(v => toGb(v) > toGb(primary)).slice(0, 2);

    let altsHtml = '';
    if (sameTierAlts.length > 0) {
      altsHtml = sameTierAlts.map(v =>
        `<li><button class="je-recommender__alt-btn" data-days="${matchedDays}" data-variant-id="${v.id}">${matchedDays}-day ${v.data}${v.unit} at ${this.formatPrice(v.id)}</button></li>`
      ).join('');
    } else {
      // No same-tier alternatives — try next tier up
      const altTier = sortedDays.find(d => d > matchedDays);
      if (altTier) {
        const alt = bestInGroup(groups[altTier]);
        if (toGb(alt) > toGb(primary)) {
          altsHtml = `<li><button class="je-recommender__alt-btn" data-days="${altTier}" data-variant-id="${alt.id}">${altTier}-day ${alt.data}${alt.unit} at ${this.formatPrice(alt.id)}</button></li>`;
        }
      }
    }

    const label = v => `${matchedDays}-day ${v.data}${v.unit} at ${this.formatPrice(v.id)}`;
    const result = el.querySelector('.je-recommender__result');
    result.innerHTML = `<p class="je-recommender__result-heading">These should do the job:<span class="je-recommender__result-sub">click to auto-select a plan</span></p><ul class="je-recommender__list"><li><button class="je-recommender__alt-btn je-recommender__primary-btn je-recommender__alt-btn--selected" data-days="${matchedDays}" data-variant-id="${primary.id}">${label(primary)}</button> <span class="je-recommender__tag">recommended</span></li>${altsHtml}</ul>`;
    result.hidden = false;

    result.querySelectorAll('.je-recommender__alt-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        result.querySelectorAll('.je-recommender__alt-btn').forEach(b =>
          b.classList.remove('je-recommender__alt-btn--selected')
        );
        btn.classList.add('je-recommender__alt-btn--selected');

        const altDays = parseInt(btn.dataset.days, 10);
        const altVariantId = btn.dataset.variantId;
        const altCard = this.cardContainer.querySelector(`[data-days="${altDays}"]`);
        if (altCard) {
          const altSelect = altCard.querySelector('.je-plan-card__select');
          if (altSelect) {
            altSelect.value = altVariantId;
            altCard.dataset.variantId = altVariantId;
            const altPriceEl = altCard.querySelector('.je-plan-card__price');
            if (altPriceEl) altPriceEl.textContent = this.formatPrice(altVariantId);
          }
          this.selectCard(altCard);
          altCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });
  }

  setupToggle() {
    if (!this.toggle) return;
    this.toggle.querySelectorAll('.je-plan-toggle__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.toggle.querySelectorAll('.je-plan-toggle__btn').forEach(b =>
          b.classList.remove('je-plan-toggle__btn--active')
        );
        btn.classList.add('je-plan-toggle__btn--active');
        this.currentMode = btn.dataset.mode;
        this.renderCards(this.currentMode);
      });
    });
  }
}

customElements.define('je-product-hero', JeProductHero);
